import SetCredentialsDialog from '@components/Activation/SetCredentialsDialog';
import LicenseFilesCard from '@components/Licenses/LicenseFilesCard';
import ConfirmDialog from '@components/Shared/ConfirmDialog';
import Header from '@components/Shared/Layout/Header';
import SeatUsageBar from '@components/Shared/SeatUsageBar';
import { expiresInText, expiryReminderSeverity } from '@constants/expiry';
import { ApplicationFeatureNames } from '@constants/features';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {
  Box, Button, Card, CardContent, Chip, IconButton, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import {
  useGetLicensesQuery, useUpdateLicenseMutation, type License
} from '@store/api/licenseEndpoints';
import { useState } from 'react';

/** Seats actually held right now, both families — what locking cuts off. */
const heldSeats = (license: License) => license.usedDeviceCount + license.usedComposerDeviceCount;

const LicensesPage = () => {
  const { data: licenses = [] } = useGetLicensesQuery();
  const [updateLicense] = useUpdateLicenseMutation();

  const [credentialsTarget, setCredentialsTarget] = useState<License>();
  const [editingLabel, setEditingLabel] = useState<{ licenseId: string, value: string }>();
  // Only locking is confirmed. Unlocking restores service, and a dialog in front of the fix for
  // an outage is a dialog somebody reads while the site is down.
  const [lockTarget, setLockTarget] = useState<License>();

  const saveLabel = async () => {
    if (!editingLabel) return;

    await updateLicense({ licenseId: editingLabel.licenseId, label: editingLabel.value });
    setEditingLabel(undefined);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Header
        title='Licenses'
        description={'Seat counts, features and expiry come from the signed license file. ' +
          'The credentials, the name and the lock are set here.'}
      />

      <LicenseFilesCard />

      {licenses.length === 0 ? (
        <Card><CardContent>
          <Typography variant='body2' color='text.secondary'>
            No licenses installed yet. Import the license file CDMVision issued for this server.
          </Typography>
        </CardContent></Card>
      ) : licenses.map(license => (
        <Card key={license.licenseId} sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction='row' spacing={2} alignItems='flex-start' justifyContent='space-between'>
              {/* minWidth: 0 — without it a long activation id or a full row of feature
                  chips would refuse to shrink and shove the fixed columns out of line. */}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant='h6'><code>{license.id ?? 'no activation id'}</code></Typography>

                  {license.locked && <Chip size='small' color='error' icon={<LockIcon />} label='Locked' />}
                  {(!license.id || !license.passwordSet) &&
                    <Chip size='small' color='warning' label='Credentials required' />}
                  {/* Both on the server's clock, like the certificate's reminders. */}
                  {license.expiresInDays <= 0 &&
                    <Chip size='small' color='error' label='Expired' />}
                  {license.expiryReminderDays !== undefined &&
                    <Chip
                      size='small'
                      color={expiryReminderSeverity(license.expiryReminderDays)}
                      label={`Expires ${expiresInText(license.expiresInDays)}`}
                    />}
                </Stack>

                {editingLabel?.licenseId === license.licenseId ? (
                  <Stack direction='row' spacing={1} sx={{ mb: 1, maxWidth: 420 }}>
                    <TextField
                      size='small'
                      value={editingLabel.value}
                      onChange={(event) => setEditingLabel({ ...editingLabel, value: event.target.value })}
                      fullWidth
                      autoFocus
                    />
                    <Button size='small' variant='contained' onClick={saveLabel}>Save</Button>
                    <Button size='small' onClick={() => setEditingLabel(undefined)}>Cancel</Button>
                  </Stack>
                ) : (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 1, cursor: 'pointer' }}
                    onClick={() => setEditingLabel({ licenseId: license.licenseId, value: license.label ?? '' })}
                  >
                    {license.label || 'Add a label…'}
                  </Typography>
                )}

                <Typography variant='caption' color='text.secondary' display='block'>
                  Expires {new Date(license.expirationDate).toFormattedString()} ·
                  {' '}issued {new Date(license.issueDate).toFormattedString()} ·
                  {' '}file <code>{license.sourceFileId.slice(0, 8)}</code>
                </Typography>

                <Stack direction='row' spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {license.applicationFeatures.length === 0 && (
                    <Typography variant='caption' color='text.secondary'>No features</Typography>
                  )}
                  {license.applicationFeatures.map(feature => (
                    <Chip
                      key={feature}
                      size='small'
                      variant='outlined'
                      label={ApplicationFeatureNames[feature] ?? feature.slice(0, 8)}
                    />
                  ))}
                </Stack>
              </Box>

              {/* Fixed width and no shrinking: the seat column has to start at the same x
                  on every card, otherwise the action column after it walks left and right. */}
              <Stack spacing={1.5} sx={{ width: 190, flexShrink: 0, pt: 0.5 }}>
                <SeatUsageBar
                  used={license.usedDeviceCount}
                  reclaimable={license.reclaimableDeviceCount}
                  limit={license.deviceLimitCount}
                  label='SuPAR'
                />
                <SeatUsageBar
                  used={license.usedComposerDeviceCount}
                  reclaimable={license.reclaimableComposerDeviceCount}
                  limit={license.composerDeviceLimitCount}
                  label='Composer'
                />
              </Stack>

              <Stack spacing={1} alignItems='flex-end' sx={{ width: 170, flexShrink: 0 }}>
                <Button size='small' onClick={() => setCredentialsTarget(license)}>
                  {license.id && license.passwordSet ? 'Change credentials' : 'Set credentials'}
                </Button>

                <Tooltip title={license.locked ? 'Unlock this license' : 'Lock this license'}>
                  <IconButton
                    size='small'
                    aria-label={license.locked ? 'Unlock license' : 'Lock license'}
                    onClick={() => license.locked
                      ? updateLicense({ licenseId: license.licenseId, locked: false })
                      : setLockTarget(license)}
                  >
                    {license.locked ? <LockIcon fontSize='small' /> : <LockOpenIcon fontSize='small' />}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}

      <SetCredentialsDialog license={credentialsTarget} onClose={() => setCredentialsTarget(undefined)} />

      {lockTarget && (
        <ConfirmDialog
          title='Lock this license?'
          confirmLabel='Lock license'
          confirmColor='warning'
          onClose={() => setLockTarget(undefined)}
          onConfirm={() => updateLicense({ licenseId: lockTarget.licenseId, locked: true })}
        >
          Every client of <code>{lockTarget.id ?? lockTarget.label ?? 'this license'}</code> is
          refused while it is locked, not only new ones. {heldSeats(lockTarget) === 0
            ? 'No device holds a seat on it at the moment.'
            : `The ${heldSeats(lockTarget)} device${heldSeats(lockTarget) === 1 ? '' : 's'} holding
               a seat now ${heldSeats(lockTarget) === 1 ? 'is' : 'are'} cut off at the next
               heartbeat, within fifteen minutes.`}
          {' '}Nothing is deleted and no seat is released: unlocking puts it back as it was.
        </ConfirmDialog>
      )}
    </Box>
  );
};

export default LicensesPage;
